import { useEffect, useState, useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import SettingsCard from "./SettingsCard";
import { Subcategory, InspectionType, CardType, AdvisorType, FieldAdvisorCardType, PromoteType, PromoteCardType } from "../constants/settingsStructure";
import { usePromptCards, PromptCardConfig } from "../hooks/useContextCards";

interface CardDetailLayoutProps {
  subcategory: Subcategory;
  disableEditing?: boolean;
}

const CardDetailLayout = ({ subcategory, disableEditing = false }: CardDetailLayoutProps) => {
  // Determine config based on subcategory properties - memoized to prevent infinite loops
  const promptConfig: PromptCardConfig = useMemo(() => {
    if (subcategory.promoteType) {
      return {
        mode: 'promote' as const,
        promoteType: subcategory.promoteType,
        promoteCardType: subcategory.promoteCardType || "role"
      };
    }
    if (subcategory.advisorType) {
      return {
        mode: 'field-advisor' as const,
        advisorType: subcategory.advisorType,
        fieldAdvisorCardType: subcategory.fieldAdvisorCardType || "persona"
      };
    }
    return {
      mode: 'context' as const,
      inspectionType: subcategory.inspectionType || "site_safety",
      cardType: subcategory.cardType || "role"
    };
  }, [subcategory.advisorType, subcategory.fieldAdvisorCardType, subcategory.inspectionType, subcategory.cardType, subcategory.promoteType, subcategory.promoteCardType]);

  const { cards, saving, canRead, canWrite, handleSave, handleDelete } = usePromptCards(
    promptConfig,
    subcategory.required || false
  );

  const [editingCardId, setEditingCardId] = useState<string | null>(null);

  type NewCardType =
    | { id: string; content: string; enabled: boolean; cardType: CardType; inspectionType: InspectionType }
    | { id: string; content: string; enabled: boolean; cardType: FieldAdvisorCardType; advisorType: AdvisorType }
    | { id: string; content: string; enabled: boolean; cardType: PromoteCardType; promoteType: PromoteType };

  const [newCards, setNewCards] = useState<Array<NewCardType>>([]);

  useEffect(() => {
    setNewCards([]);
  }, [cards]);

  // Combine permission check with disableEditing flag
  const canEdit = canWrite && !disableEditing;

  // Permission check
  if (!canRead) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">You don't have permission to view context cards. Scheduler+ role required.</p>
      </div>
    );
  }

  const handleEdit = (id: string) => {
    setEditingCardId(id);
  };

  const handleCancel = (id: string) => {
    if (id.startsWith("new-")) {
      // Remove new unsaved card from local state
      setNewCards((prev) => prev.filter((c) => c.id !== id));
    }
    // Store cards stay visible on cancel
    setEditingCardId(null);
  };

  const onSave = async (id: string, data: { content: string; enabled: boolean }) => {
    if (id.startsWith("new-")) {
      // Remove from newCards local state
      setNewCards((prev) => prev.filter((c) => c.id !== id));
    }
    await handleSave(id, data);
    setEditingCardId(null);
  };

  const onDelete = async (id: string) => {
    if (id.startsWith("new-")) {
      // Remove new unsaved card from local state
      setNewCards((prev) => prev.filter((c) => c.id !== id));
      setEditingCardId(null);
    } else {
      // Delete from store
      await handleDelete(id, allCards.length);
      setEditingCardId(null);
    }
  };

  const handleAdd = () => {
    let newCard: NewCardType;
    if (promptConfig.mode === 'context') {
      newCard = {
        id: `new-${crypto.randomUUID()}`,
        content: "",
        enabled: true,
        cardType: promptConfig.cardType,
        inspectionType: promptConfig.inspectionType
      };
    } else if (promptConfig.mode === 'field-advisor') {
      newCard = {
        id: `new-${crypto.randomUUID()}`,
        content: "",
        enabled: true,
        cardType: promptConfig.fieldAdvisorCardType,
        advisorType: promptConfig.advisorType
      };
    } else {
      newCard = {
        id: `new-${crypto.randomUUID()}`,
        content: "",
        enabled: true,
        cardType: promptConfig.promoteCardType,
        promoteType: promptConfig.promoteType
      };
    }
    setNewCards((prev) => [...prev, newCard]);
    setEditingCardId(newCard.id);
  };

  // Combine store cards with new unsaved cards
  const allCards = [...cards, ...newCards];

  const canDeleteCard = (_cardId: string): boolean => {
    if (!canEdit) return false;
    if (!subcategory.required) return true;
    return allCards.length > 1;
  };

  const canDisableCard = (cardId: string): boolean => {
    if (!canEdit) return false;
    if (!subcategory.required) return true;

    // Count enabled cards
    const enabledCards = allCards.filter(c => c.enabled);
    const currentCard = allCards.find(c => c.id === cardId);

    // If this is the last enabled card, don't allow disabling it
    if (enabledCards.length === 1 && currentCard?.enabled) {
      return false;
    }

    return true;
  };

  return (
    <div className="p-6 pb-12 relative min-h-[400px]">
      {saving && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-card p-6 rounded-lg shadow-lg flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-sm font-medium text-foreground">Saving changes...</p>
          </div>
        </div>
      )}


      <div className="space-y-4 mb-6">
        {allCards.map((card) => (
          <SettingsCard
            key={card.id}
            card={card}
            isEditMode={editingCardId === card.id}
            canDelete={canDeleteCard(card.id)}
            canDisable={canDisableCard(card.id)}
            canEdit={canEdit}
            onEdit={handleEdit}
            onSave={onSave}
            onCancel={handleCancel}
            onDelete={onDelete}
          />
        ))}
      </div>

      {allCards.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg bg-muted/30">
          <p className="text-muted-foreground mb-4">No instructions yet</p>
          <Button onClick={handleAdd} variant="outline" disabled={!canEdit}>
            <Plus className="h-4 w-4" />
            Add First Instruction
          </Button>
        </div>
      )}

      {allCards.length > 0 && (
        <Button onClick={handleAdd} variant="outline" className="w-full" disabled={!canEdit || editingCardId !== null}>
          <Plus className="h-4 w-4" />
          Add Instruction
        </Button>
      )}
    </div>
  );
};

export default CardDetailLayout;
